import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, Mail, Network, RotateCcw, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  deleteAccount,
  getMfaCode,
  listAccountChanges,
  listAccountRunLogs,
  restoreAccount,
  runAccountTask,
  updateAccount
} from "../../api/accounts";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { StatusBadge } from "../../components/status-badge";
import type { Account, RunParams } from "../../lib/types";
import { insightFromMessage } from "../../lib/task-insights";
import { copyText, formatDate, shortText } from "../../lib/utils";

const editableFields: Array<{ key: keyof Account; label: string; multiline?: boolean }> = [
  { key: "mj_id", label: "MJ ID" },
  { key: "current_email", label: "当前邮箱" },
  { key: "password", label: "密码" },
  { key: "current_email_password", label: "当前邮箱密码" },
  { key: "new_email", label: "新邮箱" },
  { key: "old_email", label: "旧邮箱" },
  { key: "old_email_password", label: "旧邮箱密码" },
  { key: "mfa_secret", label: "2FA Secret" },
  { key: "current_s5_proxy", label: "代理链接" },
  { key: "batch_no", label: "批次号" },
  { key: "warning_message", label: "警告信息", multiline: true },
  { key: "remark", label: "备注", multiline: true }
];

type AccountForm = Partial<Omit<Account, "warning_enabled">> & { warning_enabled: boolean };
type ChangeLogEntry = {
  id: number;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  created_at?: string;
};
type MfaState = {
  code: string;
  remainingSeconds: number;
  fetchedAt: number;
};

function formFromAccount(account: Account): AccountForm {
  return {
    mj_id: account.mj_id || "",
    current_email: account.current_email || "",
    password: account.password || "",
    current_email_password: account.current_email_password || "",
    new_email: account.new_email || "",
    old_email: account.old_email || "",
    old_email_password: account.old_email_password || "",
    mfa_secret: account.mfa_secret || "",
    current_s5_proxy: formatProxyValue(account.current_s5_proxy),
    batch_no: account.batch_no || "",
    warning_enabled: Boolean(account.warning_enabled),
    warning_message: account.warning_message || "",
    remark: account.remark || ""
  };
}

function patchFromForm(form: AccountForm): Partial<Account> {
  return {
    ...form,
    warning_enabled: form.warning_enabled ? 1 : 0
  };
}

function formatProxyValue(value: unknown) {
  if (!value) return "-";
  if (typeof value === "string") return value;
  const proxy = parseProxyValue(value);
  const url = proxy ? pickProxyField(proxy, ["proxy_url", "url", "endpoint", "proxy", "current_s5_proxy"]) : "";
  if (url) return url;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function parseProxyValue(value: unknown): Record<string, unknown> | null {
  if (!value) return null;
  if (typeof value === "object" && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text) return null;
  if (!/^[{[]/.test(text)) return { proxy_url: text };
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : { proxy_url: text };
  } catch {
    return { proxy_url: text };
  }
}

function pickProxyField(proxy: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = proxy[key];
    if (value !== null && value !== undefined && String(value).trim()) return String(value).trim();
  }
  return "";
}

function logLine(log: { created_at: string; level?: string; event?: string; message: string }) {
  return `[${formatDate(log.created_at)}] [${log.level || "INFO"}] [${log.event || "-"}] ${log.message}`;
}

function logLevelClass(level: unknown) {
  const value = String(level || "INFO").toUpperCase();
  if (value === "ERROR") return "border-rose-200 bg-rose-50";
  if (value === "WARNING") return "border-amber-200 bg-amber-50";
  return "border-slate-200 bg-white";
}

function secretDisplay(value: unknown, mode: "short" | "present" = "short") {
  const text = String(value ?? "");
  if (!text) return "-";
  if (mode === "present") return "已写入";
  return shortText(text, 8);
}

function isSensitiveChangeField(field: string) {
  return /token|password|secret|cookie|proxy/i.test(field);
}

function isDateChangeField(field: string) {
  return /_at$|time|date/i.test(field);
}

function changeValueLabel(field: string, value: unknown) {
  const text = String(value ?? "").trim();
  if (!text) return "-";
  if (isSensitiveChangeField(field)) return shortText(text, 12);
  if (isDateChangeField(field)) return formatDate(text);
  return text;
}

function SecretCopyRow({
  label,
  value,
  display,
  updatedAt,
  toastLabel
}: {
  label: string;
  value: unknown;
  display?: string;
  updatedAt?: string | null;
  toastLabel?: string;
}) {
  const text = String(value ?? "");
  const hasValue = Boolean(text);
  return (
    <div className="grid min-w-0 grid-cols-[auto_1fr_auto] items-center gap-2 rounded-md border bg-slate-50 px-2 py-1.5">
      <span className="text-[11px] font-medium text-slate-500">{label}</span>
      <span className="min-w-0 truncate font-mono text-[11px] text-slate-700" title={updatedAt ? `更新：${formatDate(updatedAt)}` : undefined}>
        {display ?? secretDisplay(text)}
      </span>
      <Button
        disabled={!hasValue}
        onClick={() => {
          copyText(text);
          toast.success(`${toastLabel || label}已复制`);
        }}
        className="h-7 px-2 text-[11px]"
      >
        <Copy size={12} />
        复制
      </Button>
    </div>
  );
}

const cookieInfoLabels: Record<string, string> = {
  plan: "套餐",
  status: "状态",
  subscription: "订阅",
  email: "邮箱",
  username: "用户名",
  user: "用户",
  account: "账号",
  expires_at: "过期时间",
  expiry: "过期时间",
  region: "地区",
  mode: "模式",
  relaxed: "Relaxed",
  fast: "Fast",
  remaining_time: "剩余时间",
  renew_at: "续费时间",
  runtime_exit_ip: "出口 IP",
  exit_ip: "出口 IP",
  raw: "原始信息"
};

function normalizeCookieInfo(info: unknown): unknown {
  if (typeof info !== "string") return info;
  const text = info.trim();
  if (!text) return "";
  if (!/^[{[]/.test(text)) return text;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function cookieInfoRaw(info: unknown): string {
  const normalized = normalizeCookieInfo(info);
  if (!normalized) return "";
  if (typeof normalized === "string") return normalized;
  try {
    return JSON.stringify(normalized, null, 2);
  } catch {
    return String(normalized);
  }
}

function cookieInfoValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function cookieInfoEntries(info: unknown): Array<{ label: string; value: string }> {
  const normalized = normalizeCookieInfo(info);
  if (!normalized) return [];
  if (typeof normalized === "string") {
    return normalized
      .split(/\t|\r?\n| {2,}/)
      .map((part) => part.trim())
      .filter(Boolean)
      .slice(0, 8)
      .map((part, index) => {
        const match = part.match(/^([^:=：]+)[:=：](.+)$/);
        if (match) return { label: match[1].trim(), value: match[2].trim() };
        return { label: `信息 ${index + 1}`, value: part };
      });
  }
  if (Array.isArray(normalized)) {
    return normalized.slice(0, 8).map((item, index) => ({ label: `项目 ${index + 1}`, value: cookieInfoValue(item) }));
  }
  if (typeof normalized === "object") {
    return Object.entries(normalized as Record<string, unknown>)
      .filter(([, value]) => value !== null && value !== undefined && value !== "")
      .slice(0, 8)
      .map(([key, value]) => ({ label: cookieInfoLabels[key] || key.replace(/_/g, " "), value: cookieInfoValue(value) }));
  }
  return [{ label: "信息", value: cookieInfoValue(normalized) }];
}

function CookieInfoPanel({ info, updatedAt }: { info: unknown; updatedAt?: string | null }) {
  const rows = cookieInfoEntries(info);
  const raw = cookieInfoRaw(info);
  return (
    <div className="grid gap-2 rounded-md border bg-white p-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[11px] font-semibold text-slate-500">Cookie 信息</div>
          <div className="text-[11px] text-slate-400">更新：{formatDate(updatedAt)}</div>
        </div>
        <Button
          disabled={!raw}
          onClick={() => {
            copyText(raw);
            toast.success("Cookie 信息已复制");
          }}
          className="h-7 px-2 text-[11px]"
        >
          <Copy size={12} />
          复制信息
        </Button>
      </div>
      {rows.length ? (
        <div className="grid gap-1 md:grid-cols-2 xl:grid-cols-4">
          {rows.map((row) => (
            <div key={`${row.label}:${row.value}`} className="min-w-0 rounded border bg-slate-50 px-2 py-1.5">
              <div className="text-[11px] text-slate-500">{row.label}</div>
              <div className="truncate font-mono text-[11px] text-slate-800" title={row.value}>{row.value}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded border bg-slate-50 px-2 py-1.5 text-[11px] text-slate-500">暂无 Cookie 信息</div>
      )}
      {raw ? (
        <details className="rounded border bg-slate-50 px-2 py-1.5 text-[11px]">
          <summary className="cursor-pointer text-slate-500">开发详情</summary>
          <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap break-words font-mono leading-5 text-slate-700 [overflow-wrap:anywhere]">{raw}</pre>
        </details>
      ) : null}
    </div>
  );
}

export function AccountDetailPanel({ account, runParams, onAccountChanged }: { account: Account; runParams: RunParams; onAccountChanged?: (account: Account) => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<AccountForm>(() => formFromAccount(account));
  const [mfa, setMfa] = useState<MfaState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [logLevel, setLogLevel] = useState("");
  useEffect(() => {
    setForm(formFromAccount(account));
    setMfa(null);
    setConfirmDelete(false);
  }, [account.id]);
  useEffect(() => {
    if (!mfa) return;
    const timer = window.setInterval(() => {
      setMfa((current) => {
        if (!current) return null;
        return { ...current, remainingSeconds: Math.max(0, current.remainingSeconds - 1) };
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [mfa?.fetchedAt]);
  const logs = useQuery({ queryKey: ["account-run-logs", account.id], queryFn: () => listAccountRunLogs(account.id), enabled: Boolean(account.id) });
  const changes = useQuery({ queryKey: ["account-changes", account.id], queryFn: () => listAccountChanges(account.id), enabled: Boolean(account.id) });
  const accountLogs = logs.data?.logs || [];
  const filteredAccountLogs = accountLogs.filter((log) => !logLevel || String(log.level || "INFO").toUpperCase() === logLevel);
  const saveMutation = useMutation({
    mutationFn: () => updateAccount(account.id, patchFromForm(form)),
    onSuccess: () => {
      toast.success("账号资料已保存");
      void queryClient.invalidateQueries({ queryKey: ["accounts"] });
      void queryClient.invalidateQueries({ queryKey: ["account-changes", account.id] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "保存失败")
  });
  const runMutation = useMutation({
    mutationFn: (taskType: string) => runAccountTask(account.id, taskType, runParams),
    onSuccess: () => {
      toast.success("任务已创建");
      void queryClient.invalidateQueries({ queryKey: ["accounts"] });
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "任务创建失败")
  });
  const mfaMutation = useMutation({
    mutationFn: () => getMfaCode(account.id),
    onSuccess: (data) => {
      setMfa({ code: data.code, remainingSeconds: data.remaining_seconds, fetchedAt: Date.now() });
      copyText(data.code);
      toast.success("2FA 验证码已复制");
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "获取 2FA 失败")
  });
  const deleteMutation = useMutation({
    mutationFn: () => (account.deleted_at ? restoreAccount(account.id) : deleteAccount(account.id)),
    onSuccess: (result) => {
      toast.success(account.deleted_at ? "账号已恢复" : "账号已删除");
      setConfirmDelete(false);
      onAccountChanged?.(result.account);
      void queryClient.invalidateQueries({ queryKey: ["accounts"] });
    }
  });
  const isDeleted = Boolean(account.deleted_at);
  const proxy = parseProxyValue(account.current_s5_proxy);
  const proxyExitIp = proxy ? pickProxyField(proxy, ["exit_ip", "ip", "public_ip", "runtime_exit_ip"]) : "";

  return (
    <div className="grid gap-4">
      <section className="grid gap-2 rounded-md border p-3 text-xs">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">{account.current_email}</div>
            <div className="text-slate-500">ID #{account.id} / MJ {account.mj_id || "-"}</div>
          </div>
          <StatusBadge status={account.latest_task?.status || (account.deleted_at ? "deleted" : "idle")} />
        </div>
        <div className="grid gap-2 md:grid-cols-3">
          <span>批次：{account.batch_no || "-"}</span>
          <span>Token：{shortText(account.token, 10) || "-"}</span>
          <span>Cookie：{account.mj_cookie ? "已写入" : "-"}</span>
        </div>
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          <SecretCopyRow label="密码" value={account.password} toastLabel="密码" />
          <SecretCopyRow label="邮箱密码" value={account.current_email_password} toastLabel="邮箱密码" />
          <SecretCopyRow label="2FA Secret" value={account.mfa_secret} display={secretDisplay(account.mfa_secret, "present")} toastLabel="2FA Secret" />
          <SecretCopyRow label="Token" value={account.token} updatedAt={account.token_updated_at} toastLabel="Token" />
          <SecretCopyRow label="Cookie" value={account.mj_cookie} display={secretDisplay(account.mj_cookie, "present")} updatedAt={account.cookie_updated_at} toastLabel="Cookie" />
          <SecretCopyRow label="旧邮箱密码" value={account.old_email_password} toastLabel="旧邮箱密码" />
        </div>
        <CookieInfoPanel info={account.mj_cookie_info} updatedAt={account.cookie_updated_at} />
      </section>
      <section className="flex flex-wrap gap-2">
        {isDeleted ? (
          <Button disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
            <RotateCcw size={14} />
            恢复账号
          </Button>
        ) : (
          <>
            <Button disabled={runMutation.isPending} onClick={() => runMutation.mutate("discord_login")}>
              <KeyRound size={14} />
              Discord 登录
            </Button>
            <Button disabled={runMutation.isPending} onClick={() => runMutation.mutate("discord_protocol_login")}>
              <Network size={14} />
              Discord 协议登录
            </Button>
            <Button disabled={runMutation.isPending} onClick={() => runMutation.mutate("discord_email")}>
              <Mail size={14} />
              Discord 更换邮箱
            </Button>
            <Button disabled={runMutation.isPending} onClick={() => runMutation.mutate("mj_login")}>
              <Network size={14} />
              Midjourney 协议登录
            </Button>
            <Button disabled={mfaMutation.isPending} onClick={() => mfaMutation.mutate()}>
              获取 2FA
            </Button>
            <Button onClick={() => setConfirmDelete(true)} className="border-rose-300 text-rose-700">
              <Trash2 size={14} />
              删除账号
            </Button>
          </>
        )}
      </section>
      {mfa ? (
        <section className="grid gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[11px] font-medium text-emerald-700">当前 2FA 验证码</div>
              <div className="font-mono text-2xl font-semibold tracking-widest">{mfa.code}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] text-emerald-700">剩余</div>
              <div className="text-xl font-semibold">{mfa.remainingSeconds}s</div>
            </div>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-emerald-100">
            <div className="h-full rounded-full bg-emerald-600 transition-all" style={{ width: `${Math.max(0, Math.min(100, (mfa.remainingSeconds / 30) * 100))}%` }} />
          </div>
        </section>
      ) : null}
      <section className="grid gap-3 rounded-md border p-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold text-slate-500">账号资料</h3>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={Boolean(form.warning_enabled)}
              onChange={(event) => setForm((current) => ({ ...current, warning_enabled: event.target.checked }))}
            />
            启用警告
          </label>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {editableFields.map((field) => (
            <label key={String(field.key)} className={field.multiline ? "grid gap-1 text-xs md:col-span-2" : "grid gap-1 text-xs"}>
              {field.label}
              {field.multiline ? (
                <Textarea
                  rows={3}
                  value={String(form[field.key] || "")}
                  onChange={(event) => setForm((current) => ({ ...current, [field.key]: event.target.value }))}
                />
              ) : (
                <Input value={String(form[field.key] || "")} onChange={(event) => setForm((current) => ({ ...current, [field.key]: event.target.value }))} />
              )}
            </label>
          ))}
        </div>
        <div className="flex justify-end">
          <Button disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()} className="bg-slate-900 text-white hover:bg-slate-800">
            <Save size={14} />
            保存账号资料
          </Button>
        </div>
      </section>
      <section className="grid gap-2 rounded-md border p-3 text-xs">
        <div className="flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 font-semibold text-slate-500">
            <Network size={14} />
            代理
          </h3>
          <Button
            onClick={() => {
              copyText(formatProxyValue(account.current_s5_proxy));
              toast.success("当前代理已复制");
            }}
          >
            <Copy size={13} />
            复制当前
          </Button>
        </div>
        <div className="grid gap-2 md:grid-cols-[1fr_160px_160px]">
          <div className="min-w-0 rounded-md border bg-slate-50 p-2">
            <div className="mb-1 text-[11px] font-medium text-slate-500">当前代理</div>
            <div className="break-words font-mono leading-5 [overflow-wrap:anywhere]">{formatProxyValue(account.current_s5_proxy)}</div>
          </div>
          <div className="rounded-md border bg-slate-50 p-2">
            <div className="mb-1 text-[11px] font-medium text-slate-500">出口 IP</div>
            <div className="break-words font-mono leading-5 [overflow-wrap:anywhere]">{proxyExitIp || "-"}</div>
          </div>
          <div className="rounded-md border bg-slate-50 p-2 md:w-40">
            <div className="mb-1 text-[11px] font-medium text-slate-500">获取时间</div>
            <div>{formatDate(account.current_s5_proxy_fetched_at)}</div>
          </div>
        </div>
      </section>
      <section className="grid gap-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-semibold text-slate-500">运行日志</h3>
          <div className="flex items-center gap-2">
            <select className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs" value={logLevel} onChange={(event) => setLogLevel(event.target.value)}>
              <option value="">全部级别</option>
              <option value="INFO">INFO</option>
              <option value="WARNING">WARNING</option>
              <option value="ERROR">ERROR</option>
            </select>
            <Button
              onClick={() => {
                copyText(filteredAccountLogs.map(logLine).join("\n"));
                toast.success("运行日志已复制");
              }}
            >
              <Copy size={13} />
              复制全部
            </Button>
          </div>
        </div>
        <div className="max-h-72 overflow-auto rounded-md border">
          {filteredAccountLogs.length ? (
            filteredAccountLogs.map((log) => {
              const insight = insightFromMessage(log.message);
              return (
                <div key={log.id} className={`border-b p-2 text-xs last:border-b-0 ${logLevelClass(log.level)}`}>
                  <div className="mb-1 flex items-center justify-between gap-2 text-slate-500">
                    <span>{log.level || "INFO"} / {log.event || "-"}</span>
                    <span className="flex items-center gap-2">
                      {formatDate(log.created_at)}
                      <button
                        className="text-slate-500 hover:text-slate-900"
                        onClick={() => {
                          copyText(logLine(log));
                          toast.success("单条日志已复制");
                        }}
                      >
                        <Copy size={12} />
                      </button>
                    </span>
                  </div>
                  <div className="whitespace-pre-wrap break-words leading-5 [overflow-wrap:anywhere]">{log.message}</div>
                  {insight?.hint ? (
                    <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-amber-800">
                      {insight.stage}：{insight.hint}
                    </div>
                  ) : null}
                </div>
              );
            })
          ) : (
            <div className="p-2 text-xs text-slate-500">{logs.isLoading ? "运行日志加载中..." : "暂无运行日志"}</div>
          )}
        </div>
      </section>
      <section className="grid gap-2">
        <h3 className="text-xs font-semibold text-slate-500">变动日志</h3>
        <div className="max-h-56 overflow-auto rounded-md border">
          {(changes.data?.logs || []).map((log) => {
            const change = log as unknown as ChangeLogEntry;
            const field = String(change.field_name || "");
            return (
              <div key={change.id} className="grid gap-1 border-b p-2 text-xs last:border-b-0">
                <div className="flex min-w-0 items-center justify-between gap-2">
                  <span className="min-w-0 break-words font-medium text-slate-600 [overflow-wrap:anywhere]">{field || "-"}</span>
                  <span className="shrink-0 text-[11px] text-slate-400">{formatDate(change.created_at)}</span>
                </div>
                <div className="grid min-w-0 items-start gap-1 rounded bg-slate-50 p-2 text-slate-800 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
                  <span className="min-w-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere]" title={String(change.old_value || "")}>
                    {changeValueLabel(field, change.old_value)}
                  </span>
                  <span className="px-1 text-slate-400">→</span>
                  <span className="min-w-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere]" title={String(change.new_value || "")}>
                    {changeValueLabel(field, change.new_value)}
                  </span>
                </div>
              </div>
            );
          })}
          {changes.data?.logs?.length === 0 ? <div className="p-2 text-xs text-slate-500">暂无变动日志</div> : null}
        </div>
      </section>
      {confirmDelete ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/30 p-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="border-b p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-rose-700">
                <Trash2 size={16} />
                确认删除账号
              </div>
              <p className="mt-2 text-xs leading-5 text-slate-600">这会软删除账号，worker 操作会被禁用；账号数据仍保留，可从已删除筛选中恢复。</p>
            </div>
            <div className="space-y-2 p-4 text-xs">
              <div className="rounded-md border bg-slate-50 p-2">账号：{account.current_email}</div>
              <div className="rounded-md border bg-slate-50 p-2">ID：#{account.id}</div>
            </div>
            <div className="flex justify-end gap-2 border-t p-3">
              <Button onClick={() => setConfirmDelete(false)}>取消</Button>
              <Button disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate()} className="border-rose-200 bg-rose-600 text-white hover:bg-rose-700">
                确认删除
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
